import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppSelector } from '@/store/hooks';
import { createUser, userQueryKeys } from '@/features/users/api';
import { CreateUserSchema } from '@/features/users/user';
import {
  getRoleTemplates,
  roleTemplateQueryKeys,
} from '@/features/users/permission-templates';
import { ApiError } from '@/lib/api';
import { hasPermission } from '@/lib/auth/permissions';
import { getTenantOptions, getBranchOptions } from '@/features/value-lists/api';
import type {
  ValueListItem,
  BranchValueListItem,
} from '@/features/value-lists/types';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserPlus, ArrowLeft } from '@/lib/icons';
import { PageShell } from '@/components/layout/PageShell';
import { useToast } from '@/hooks/use-toast';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const createUserFormSchema = (t: (key: string) => string) =>
  CreateUserSchema.extend({
    firstName: z.string().min(2, t('common.validation.firstNameRequired')),
    lastName: z.string().min(2, t('common.validation.lastNameRequired')),
    email: z
      .string()
      .min(1, t('users.new.validation.emailRequired'))
      .email(t('common.validation.emailInvalid')),
    phoneNumber: z.string().optional(),
    password: z.string().min(8, t('users.new.validation.passwordMinLength')),
    roleTemplateId: z.string().min(1, t('users.new.validation.roleTemplateRequired')),
    tenantId: z.string().nullable().optional(),
    branchId: z.string().nullable().optional(),
  });

type UserFormValues = z.infer<ReturnType<typeof createUserFormSchema>>;

export const Route = createFileRoute('/_layout/users/new')({
  component: NewUserPage,
});

function NewUserPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tenants, setTenants] = useState<ValueListItem[]>([]);
  const [branches, setBranches] = useState<BranchValueListItem[]>([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  const currentUser = useAppSelector((state) => state.auth.user);
  // The template is the only access control the create form sends: platform
  // derives the userType, the access tier and the permission matrix from it,
  // so a new user is never created with a matrix nobody chose.
  const { data: templatesResult } = useQuery({
    queryKey: roleTemplateQueryKeys.all,
    queryFn: getRoleTemplates,
    staleTime: Infinity,
  });
  const templates = templatesResult?.value ?? [];
  const canPickTenant = hasPermission(currentUser, 'tenants', 'update');

  const userFormSchema = createUserFormSchema(t);

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: 'Password123!',
      roleTemplateId: 'agent',
      tenantId: null,
      branchId: null,
    },
  });

  const selectedTenantId = form.watch('tenantId');
  const selectedTemplate = templates.find(
    (tpl) => tpl.id === form.watch('roleTemplateId')
  );

  useEffect(() => {
    if (canPickTenant) {
      getTenantOptions().then((result) => {
        if (result.succeeded && result.value) {
          setTenants(result.value);
        }
      });
    }
  }, [canPickTenant]);

  useEffect(() => {
    if (canPickTenant && !selectedTenantId) return;
    const tenantId = canPickTenant
      ? (selectedTenantId ?? undefined)
      : undefined;
    getBranchOptions(tenantId).then((result) => {
      if (result.succeeded && result.value) {
        setBranches(result.value);
      }
    });
  }, [canPickTenant, selectedTenantId]);

  const onSubmit = async (values: UserFormValues) => {
    setIsSubmitting(true);
    try {
      const result = await createUser({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        roleTemplateId: values.roleTemplateId,
        password: values.password,
        tenantId: values.tenantId ?? undefined,
        branchId: values.branchId ?? undefined,
      });

      if (!result.succeeded) {
        toast({
          title: t('common.error'),
          description:
            result.errors?.join(', ') || t('users.new.toast.error.description'),
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: t('common.success'),
        description: t('users.new.toast.success.description', {
          firstName: values.firstName,
          lastName: values.lastName,
        }),
      });
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      form.reset();
      if (result.value?.id) {
        navigate({ to: `/users/${result.value.id}` });
      } else {
        navigate({ to: '/users' });
      }
    } catch (error) {
      const description =
        error instanceof ApiError
          ? error.errors?.[0] || error.message
          : error instanceof Error
            ? error.message
            : t('users.new.toast.error.description');
      toast({
        title: t('common.error'),
        description,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAuthorized = hasPermission(currentUser, 'users', 'create');

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1 className="text-2xl font-bold mb-4">
          {t('common.permissions.accessDenied')}
        </h1>
        <p className="text-muted-foreground">
          {t('common.permissions.noPermission')}
        </p>
      </div>
    );
  }

  return (
    <PageShell
      title={t('users.new.title')}
      description={t('users.new.description')}
      actions={
        <Button variant="outline" onClick={() => navigate({ to: '/users' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.actions.back')}
        </Button>
      }
    >
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardContent className="pt-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common.form.firstName')} *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('common.form.firstName')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common.form.lastName')} *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('common.form.lastName')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common.form.email')} *</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t('common.form.email')}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common.form.phoneNumber')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t(
                              'users.new.form.phoneNumberPlaceholder'
                            )}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.form.password')} *</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder={t('users.new.form.passwordPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="roleTemplateId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.role')}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ''}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                'users.permissions.template.placeholder'
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {templates.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.id}>
                              {tpl.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {selectedTemplate?.description ??
                          t('users.permissions.template.description')}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {canPickTenant && (
                  <FormField
                    control={form.control}
                    name="tenantId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common.form.tenant')}</FormLabel>
                        <Select
                          onValueChange={(val) => {
                            field.onChange(val);
                            form.setValue('branchId', null);
                          }}
                          value={field.value ?? ''}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t(
                                  'users.new.form.tenantPlaceholder'
                                )}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {tenants.map((tenant) => (
                              <SelectItem key={tenant.id} value={tenant.id}>
                                {tenant.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="branchId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.form.branch')}</FormLabel>
                      <Select
                        onValueChange={(val) => field.onChange(val)}
                        value={field.value ?? ''}
                        disabled={canPickTenant && !selectedTenantId}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                'users.new.form.branchPlaceholder'
                              )}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {branches.map((branch) => (
                            <SelectItem key={branch.id} value={branch.id}>
                              {branch.name}
                              {branch.city ? ` - ${branch.city}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-between pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate({ to: '/users' })}
                    disabled={isSubmitting}
                  >
                    {t('common.actions.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="min-w-32"
                  >
                    <UserPlus className="mr-1 h-4 w-4" />
                    {isSubmitting
                      ? t('common.loading.creating')
                      : t('common.actions.create')}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}
